import { useEffect, useMemo, useRef, useState } from 'react';
import { MdSend, MdAttachFile, MdClose, MdInsertDriveFile } from 'react-icons/md';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Button } from '@/components/Button';
import { Textarea } from '@/components/Textarea';
import Loading from '@/components/Loading';
import type { AgentApiCandidateChatMessagesRecord, SharedFile } from '@/types/serializers';
import useFetchCandidateChatMessages from '../api/useFetchCandidateChatMessages';
import useCreateCandidateChatMessage from '../api/useCreateCandidateChatMessage';
import useReadCandidateChatMessages from '../api/useReadCandidateChatMessages';
import { showErrorToast } from '../../job_change_processes/lib/toast';

type Props = {
  candidateId: number;
  candidateName: string;
};

// 添付可能なファイルサイズ（10MB、バックエンドの上限に合わせる）
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const formatDateChip = (iso: string) => {
  const d = new Date(iso);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mm}/${dd}`;
};

const formatTime = (iso: string) => {
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
};

// split 用（グローバル）と判定用（非グローバル）で正規表現を分ける。
// グローバル正規表現は test() のたびに lastIndex が進み判定が不安定になるため。
const URL_SPLIT_REGEX = /(https?:\/\/[^\s]+)/g;
const URL_TEST_REGEX = /^https?:\/\/[^\s]+$/;

// 本文中の URL をリンクに変換する
function renderContent(content: string) {
  return content.split(URL_SPLIT_REGEX).map((part, index) => {
    if (URL_TEST_REGEX.test(part)) {
      return (
        <a key={index} href={part} target="_blank" rel="noopener noreferrer" className="underline">
          {part}
        </a>
      );
    }
    return part;
  });
}

// 添付ファイルは枠付きチップ（アイコン＋ファイル名）で表示する
function FileChip({ file }: { file: SharedFile }) {
  return (
    <a
      href={file.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex h-[42px] w-fit max-w-full items-center gap-xs rounded border border-solid border-border bg-white px-sm"
    >
      <MdInsertDriveFile size={20} className="shrink-0 text-main" />
      <span className="truncate text-body-sm font-bold text-main">{file.filename}</span>
    </a>
  );
}

function MessageBubble({
  message,
  showSenderName,
}: {
  message: AgentApiCandidateChatMessagesRecord;
  showSenderName: boolean;
}) {
  const isOutgoing = message.sender_kind === 'organization_user';
  const time = formatTime(message.created_at);
  const hasContent = (message.content?.trim().length ?? 0) > 0;

  // 本文バブルと添付チップを縦に積む（どちらか一方のみのこともある）
  const body = (
    <div className={clsx('flex flex-col gap-xxs', isOutgoing ? 'items-end' : 'items-start')}>
      {hasContent && (
        <div
          style={{ maxWidth: 340 }}
          className={clsx(
            'rounded-md p-sm text-body-sm leading-5 break-words whitespace-pre-wrap',
            isOutgoing
              ? 'bg-blue-100 text-blue-950'
              : 'border border-solid border-border bg-white text-main'
          )}
        >
          {renderContent(message.content ?? '')}
        </div>
      )}
      {message.file && <FileChip file={message.file} />}
    </div>
  );

  if (isOutgoing) {
    return (
      <div className="flex w-full flex-col items-end gap-xxs pl-xl">
        {showSenderName && message.sender_name && (
          <p className="text-body-xs text-sub">{message.sender_name}</p>
        )}
        <div className="flex w-fit max-w-full items-end gap-xxs">
          <div className="flex shrink-0 flex-col text-right text-body-xs leading-4 whitespace-nowrap text-sub">
            {message.read_at && <span>既読</span>}
            <span>{time}</span>
          </div>
          {body}
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col items-start gap-xxs pr-xl">
      <div className="flex w-fit max-w-full items-end gap-xxs">
        {body}
        <span className="shrink-0 text-body-xs leading-4 whitespace-nowrap text-sub">{time}</span>
      </div>
    </div>
  );
}

export default function CandidatesChatCard({ candidateId, candidateName }: Props) {
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage, refetch } =
    useFetchCandidateChatMessages(candidateId);
  const { mutateAsync: createMessage, isPending: isSending } = useCreateCandidateChatMessage();
  const { mutate: readMessages } = useReadCandidateChatMessages();
  const queryClient = useQueryClient();

  const [content, setContent] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const prependPrevHeightRef = useRef<number | null>(null);
  const didInitialScrollRef = useRef(false);

  // API は新しい順で返るため、全ページを結合して古い順に並べ替える
  const messages = useMemo(() => {
    const all = data?.pages.flatMap((page) => page.records) ?? [];
    return [...all].reverse();
  }, [data]);

  const totalCount = data?.pages[0]?.total_count ?? 0;

  // 初回ロード時に最下部へスクロールし、候補者からのメッセージを既読化する
  useEffect(() => {
    if (!isLoading && !didInitialScrollRef.current && messages.length > 0) {
      didInitialScrollRef.current = true;
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      });
    }
  }, [isLoading, messages.length]);

  useEffect(() => {
    if (!isLoading && totalCount > 0) {
      readMessages(candidateId);
    }
  }, [isLoading, totalCount, candidateId, readMessages]);

  // 過去メッセージを先頭に追加した際にスクロール位置を保持する
  useEffect(() => {
    if (prependPrevHeightRef.current !== null && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight - prependPrevHeightRef.current;
      prependPrevHeightRef.current = null;
    }
  }, [messages.length]);

  const handleScroll = () => {
    if (!scrollRef.current) return;
    if (scrollRef.current.scrollTop <= 40 && hasNextPage && !isFetchingNextPage) {
      prependPrevHeightRef.current = scrollRef.current.scrollHeight;
      fetchNextPage();
    }
  };

  // テキストエリアの高さを内容に合わせて自動調整する
  const adjustTextareaHeight = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [content]);

  const handleSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    if (selected.size > MAX_FILE_SIZE) {
      showErrorToast('ファイルサイズは10MBまでです');
      e.target.value = '';
      return;
    }
    setFile(selected);
    e.target.value = '';
  };

  const canSend = (content.trim().length > 0 || !!file) && !isSending;

  const handleSend = async () => {
    if (!canSend) return;
    try {
      await createMessage({ candidateProfileId: candidateId, content: content.trim(), file });
      setContent('');
      setFile(null);
      await queryClient.invalidateQueries({ queryKey: ['candidateChatMessages', candidateId] });
      await refetch();
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      });
    } catch {
      showErrorToast('メッセージの送信に失敗しました');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      style={{ width: 480 }}
      className="flex h-full min-h-0 shrink-0 flex-col overflow-hidden rounded bg-white shadow-[0px_0px_6px_0px_#00000033]"
    >
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="hidden-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto px-md pt-md"
      >
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <Loading size="small" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-md text-center">
            <div className="flex size-20 items-center justify-center rounded-full bg-gray text-placeholder">
              <MdSend size={40} />
            </div>
            <div>
              <p className="text-heading-xs text-main">メッセージがありません</p>
              <p className="mt-xs text-body-xs text-sub">
                {candidateName}さんとの
                <br />
                最初のメッセージを送信しましょう
              </p>
            </div>
          </div>
        ) : (
          <div className="flex min-h-full flex-col gap-md">
            {/* メッセージが少ないときは最下部に寄せ、多いときは flex-1 が縮んで上方向スクロール可能にする */}
            <div className="min-h-0 flex-1" aria-hidden="true" />
            {isFetchingNextPage && (
              <div className="flex justify-center py-xs">
                <Loading size="small" />
              </div>
            )}
            {messages.map((message, index) => {
              const prev = index > 0 ? messages[index - 1] : null;
              const showDate =
                !prev || formatDateChip(prev.created_at) !== formatDateChip(message.created_at);
              const showSenderName =
                message.sender_kind === 'organization_user' &&
                (showDate ||
                  !prev ||
                  prev.sender_kind !== 'organization_user' ||
                  prev.sender_name !== message.sender_name);

              return (
                <div key={message.id} className="flex flex-col gap-md">
                  {showDate && (
                    <div className="flex justify-center">
                      <span className="rounded-full bg-gray px-md py-xxs text-body-xs leading-4 text-sub">
                        {formatDateChip(message.created_at)}
                      </span>
                    </div>
                  )}
                  <MessageBubble message={message} showSenderName={showSenderName} />
                </div>
              );
            })}
            {/* overflow スクロールコンテナは padding-bottom が最下端で描画されないため、余白を実要素で確保する */}
            <div aria-hidden="true" className="h-md shrink-0" />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-xs border-t border-solid border-border p-md">
        {file && (
          <div className="flex items-center gap-xxs self-start rounded-sm border border-solid border-border bg-gray px-xs py-xxs text-body-xs text-main">
            <MdInsertDriveFile size={14} />
            <span className="max-w-[260px] truncate">{file.name}</span>
            <button
              type="button"
              className="flex items-center text-sub hover:opacity-60"
              onClick={() => setFile(null)}
              aria-label="添付ファイルを削除"
            >
              <MdClose size={14} />
            </button>
          </div>
        )}
        <Textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="メッセージを入力..."
          className="max-h-[160px] min-h-[48px] resize-none"
          rows={1}
        />
        <div className="flex items-center justify-between">
          <Button
            type="button"
            color="secondary"
            size="md"
            iconOnly
            onClick={() => fileInputRef.current?.click()}
            aria-label="ファイルを添付"
          >
            <MdAttachFile size={20} />
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.doc,.docx"
            className="hidden"
            onChange={handleSelectFile}
          />
          <Button type="button" color="primary" size="md" disabled={!canSend} onClick={handleSend}>
            送信
            <MdSend size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
}
